<template>
	<main :data-test="$route.path">
		<header>
			<div class="relative grid min-h-495 tablet:min-h-720 tabletWide:min-h-696 desktop:min-h-618">
				<div class="tablet:mx-48 desktop:mx-56">
					<div class="pt-16 tablet:pt-24 pb-32 tablet:pb-24 px-16 tablet:px-48 desktop:px-56 max-w-375 tablet:max-w-none desktop:mx-auto desktop:max-w-1440 flex flex-col h-full space-y-24">
						<breadCrumb v-bind="pageContent.pageHeader.breadcrumb" />
						<div class=" flex-1 flex flex-col justify-end tablet:justify-center space-y-24 tablet:space-y-16 tabletWide:space-y-32">
							<h1 class="flex flex-col text-white font-bold space-y-6 tablet:space-y-12 ">
								<span class="text-20 tablet:text-24">{{ pageContent.pageHeader.titleTag }}</span>
								<span class="text-40 tablet:text-64" data-test="title">{{ pageContent.pageHeader.title }}</span>
							</h1>
							<p>
								<a v-if="pageContent.pageHeader.headlineLink" :href="pageContent.pageHeader.headlineLink" class="hover:opacity-90 text-white text-18 tablet:text-22">
									<inlineMarkdown :markdown="pageContent.pageHeader.headline" />
								</a>
								<inlineMarkdown v-else :markdown="pageContent.pageHeader.headline" class="text-white text-18 tablet:text-22" />
							</p>
							<div class="flex items-center tabletWide:items-start desktop:items-center tabletWide:flex-col desktop:flex-row tablet:space-x-32 tabletWide:space-x-16 tabletWide:space-y-32 desktop:space-y-0 ">
								<formItinerary theme="ghost" :showLabels="true" :branch-picker-start-code="pageContent.location.code" :branch-picker-end-code="pageContent.location.code" class="bg-white/40 rounded-GO p-16 w-full tablet:w-8/12 tabletWide:w-full desktop:w-10/12 desktop:p-24 desktopWide:w-9/12" />
								<endorsementsHeader />
							</div>
						</div>
					</div>
				</div>
				<figure class="absolute inset-0 -z-10 rounded-GO overflow-hidden tablet:ml-48 desktop:ml-56">
					<LazyHydrate never>
						<goPicture v-once v-bind="pageContent.pageHeader.picture" :enable-placeholder="false" class="h-full" />
					</LazyHydrate>
				</figure>
				<div class="absolute inset-0 -z-10 rounded-GO overflow-hidden tablet:ml-48 desktop:ml-56 bg-goPageHeaderGradientMobile tablet:bg-goPageHeaderGradientTablet tabletWide:bg-goPageHeaderGradientTabletWide desktop:bg-goPageHeaderGradientDesktop desktopWide:bg-goPageHeaderGradientDesktopWide"></div>
			</div>
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection class="p-16 group bg-goPink hover:bg-goPinkDark transition-colors rounded-GO tablet:rounded-tr-0 cursor-pointer">
					<subscribeBanner />
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection type="post-reversed-flip">
					<template #main>
						<layoutPost type="start">
							<branchInfo v-bind="pageContent.location" :path="$route.path"/>
							<hr class="border-black/10" />
							<titleAndContent :title="pageContent.branchInformationTitle" :content="pageContent.branchInformationContent">
								<p>{{ pageContent.branchInformationTitle }}</p>
							</titleAndContent>
							<titleAndContent v-bind="pageContent.affordableCarRental">
								<p>{{ pageContent.affordableCarRental.title }}</p>
							</titleAndContent>
						</layoutPost>
					</template>
					<template #aside>
						<div class="p-16 tablet:p-0 space-y-32">
							<branchMap v-bind="pageContent.location" />
							<div class="space-y-16 tablet:space-y-24">
								<faqAccordion v-for="(faq, index) in pageContent.branchFaqs" :id="`9999${index + 1}`" :key="index">
									<template #question>{{ faq.question }}</template>
									<template #answer>
										<span v-html="faq.answer" />
									</template>
								</faqAccordion>
							</div>
						</div>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-if="$store.state.reviews.reviews.length" when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 grid grid-cols-12 items-center tablet:items-stretch tabletWide:gap-16">
						<allReviews class=" col-span-8 tablet:col-span-10 tablet:col-span-9 desktop:col-span-6" />
						<allEndorsements class=" h-full border-black/10 border-l col-span-4 tablet:col-span-2 tablet:col-span-3 desktop:col-span-6" />
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<googleReviewsWidget :locationWidgetId="pageContent.reviewsWidgetId" :title="`Customer reviews of ${pageContent.pageHeader.title}`" />
			</LazyHydrate>

			<LazyHydrate when-visible>
				<customerQuickFacts />
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-24 desktop:text-32">Popular GO Rentals cars</h2>
							</header>
						</div>
						<vehicleGroupsSection class="grid grid-cols-2 gap-16 tablet:grid-cols-3 desktop:grid-cols-7" />
					</div>
				</layoutSection>
			</LazyHydrate>

			<whyGoWithUsSection />

			<totalCareSection />

			<LazyHydrate when-visible>
				<layoutSection type="post">
					<template #main>
						<layoutPost type="left">
							<header>
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">GO Rentals App for a ridiculously simple pick-up and drop-off</h2>
							</header>
							<p class="text-18 desktop:text-22">Get your journey started as quickly as possible thanks to our speedy check-in. Manage your booking online or download our app to get through the check-in process and on the road faster.</p>
							<footer>
								<nuxt-link to="/app/" class="button button-goBlueGhost button-normal">Download App</nuxt-link>
							</footer>
						</layoutPost>
					</template>
					<template #aside>
						<figure class="h-full flex items-center ">
							<videoYouTube class="w-full" v-bind="{
								image: { mobile: { src: '/images/Simple_PU_DO.jpg' }, alt: 'Image of a GO Rentals branch showing its self-service kiosks' },
								title: 'The GO Experience',
								src: 'https://www.youtube.com/embed/KjChqxbIHoY',
								duration: '1:58',
								ratio: '3:2',
							}" />
						</figure>
					</template>
				</layoutSection>
			</LazyHydrate>

			<dealsSection>
				<template #title>
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ pageContent.pageHeader.title }} Car Rental Deals</h2>
				</template>
				<template #intro>
					<p class="text-18 desktop:text-22" v-html="pageContent.dealsContent" />
				</template>
			</dealsSection>

			<insuranceSection />

			<LazyHydrate when-visible>
				<layoutSection class="p-16 tablet:p-0">
					<faqList :faqs="pageContent.faqs" :readMoreLink="pageContent.readMoreFaqsLink" />
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Explore {{ pageContent.branchLocationName }} with GO Rentals</h2>
							</header>
							<div class="tablet:w-8/12 ">
								<p class="text-18 desktop:text-22" v-html="pageContent.exploreWithGoRentals.content" />
							</div>
						</div>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-3 desktop:gap-16">
							<li v-for="article in pageContent.exploreWithGoRentals.articles" :key="article.title" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<img class="bg-gray-50 object-cover w-full h-full" :src="$cloudFlare.getImage(article.image)" width="432" height="224" loading="lazy" :alt="article.alt" />
									</figure>
									<div class="p-16 space-y-16">
										<header>
											<h3 class="font-bold text-18">{{ article.title }}</h3>
										</header>
										<p class="mb-16" v-html="article.content" />
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap space-y-32">
							<header class="tablet:w-8/12">
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Trust Thousands of Snap Happy Customers</h2>
							</header>
							<div class="tablet:w-8/12 ">
								<p class="text-18 desktop:text-22">Our <nuxt-link to="/go-snap-happy/" class="link link-goBlue font-bold">Snap Happy Photo Competition</nuxt-link> has customers revv’d up about our {{ pageContent.pageHeader.title }} car hire in New Zealand! Submit a cool photo of your GO travel memory and be in with a chance to WIN up to $500 back from your booking.</p>
							</div>
						</div>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16">
							<li v-for="snapHappy in pageContent.snapHappy" :key="snapHappy.name" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<img :src="$cloudFlare.getImage(snapHappy.image)" width="284" height="238" loading="lazy" decoding="async" :alt="snapHappy.alt" class="bg-gray-50 object-cover w-full h-full">
									</figure>
									<div class="p-16 space-y-16">
										<header>
											<h3 class="font-bold">{{ snapHappy.name }}</h3>
										</header>
										<p class="mb-16">{{ snapHappy.text }}</p>
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<titleAndContent v-bind="pageContent.startYourAdventure">
							<h3>{{ pageContent.startYourAdventure.title }}</h3>
						</titleAndContent>
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<secondaryMenu :links="pageContent.secondaryMenu" />
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection class="bg-goSkyBright bg-nzMapBackground tabletWide:bg-nzMapBackground-reversed rounded-GO tablet:rounded-tr-0">
					<goAllAcrossNZ>
						<template #title>
							<p class="font-bold text-32 tablet:text-40 desktop:text-48 whitespace-nowrap">GO all across NZ</p>
						</template>
					</goAllAcrossNZ>
				</layoutSection>
			</LazyHydrate>
		</div>
	</main>
</template>

<script>
import inlineMarkdown from "~/components/inlineMarkdown.vue"
import secondaryMenu from "~/components/secondaryMenu.vue"
import goAllAcrossNZ from "~/components/goAllAcrossNZ.vue"
import titleAndContent from "~/components/titleAndContent.vue"
import branchInfo from "~/components/branchInfo.vue"
import breadCrumb from '~/components/breadCrumb.vue'
import layoutSection from '~/components/layoutSection.vue'
import layoutPost from '~/components/layoutPost.vue'
import endorsementsHeader from "~/components/endorsementsHeader.vue"
import { getSchemaFaqPage } from '~/assets/scripts/utilities/getStructuredData'
import { getSlug } from '~/assets/scripts/utilities/getSlug'
import { getPageMetaData } from '~/assets/scripts/utilities/getPageMetaData'

export default {
	getSlug,
	components: {
		layoutSection,
		layoutPost,
		inlineMarkdown,
		secondaryMenu,
		goAllAcrossNZ,
		titleAndContent,
		branchInfo,
		breadCrumb,
		endorsementsHeader,
		vehicleGroupsSection: () => import("~/components/vehicleGroupsSection.vue"),
		formItinerary: () => import("~/components/formItinerary.vue"),
		faqList: () => import("~/components/faqList.vue"),
		faqAccordion: () => import("~/components/faqAccordion.vue"),
		videoYouTube: () => import("~/components/videoYouTube.vue"),
		customerQuickFacts: () => import("~/components/customerQuickFacts.vue"),
		allReviews: () => import("~/components/allReviews.vue"),
		allEndorsements: () => import("~/components/allEndorsements.vue"),
		branchMap: () => import("~/components/branchMap.vue"),
		subscribeBanner: () => import("~/components/subscribeBanner.vue"),
		dealsSection: () => import("~/components/dealsSection.vue"),
		whyGoWithUsSection: () => import("~/components/whyGoWithUsSection.vue"),
		totalCareSection: () => import("~/components/totalCareSection.vue"),
		insuranceSection: () => import("~/components/insuranceSection.vue"),
		googleReviewsWidget: () => import("~/components/googleReviewsWidget.vue"),
	},
	metaInfo() {
		const { slug } = this.$route.params
		const { picture } = this.pageContent.pageHeader
		return {
			title: this.pageMeta.title,
			meta: [
				{
					vmid: "description",
					name: "description",
					content: this.pageMeta.description,
				},
				{ vmid: "ogtitle", property: "og:title", content: this.pageMeta.title },
				{ vmid: "ogurl", property: "og:url", content: `https://www.gorentals.co.nz/rental-car-locations/${slug}/` },
			],
			link: [
				{ rel: "canonical", href: `https://www.gorentals.co.nz/rental-car-locations/${slug}/` },
				{ rel: "preload", href: this.$cloudFlare.getImage(picture.desktopWide.src, { width: picture.desktopWide.width, height: picture.desktopWide.height }), as: 'image', media: `(min-width: 100em)`, fetchpriority: "high" },
				{ rel: "preload", href: this.$cloudFlare.getImage(picture.desktop.src, { width: picture.desktop.width, height: picture.desktop.height }), as: 'image', media: `(min-width: 80em) and (max-width: ${((100 * 16) - 1) / 16}em)`, fetchpriority: "high" },
				{ rel: "preload", href: this.$cloudFlare.getImage(picture.tabletWide.src, { width: picture.tabletWide.width, height: picture.tabletWide.height }), as: 'image', media: `(min-width: 62.5em) and (max-width: ${((80 * 16) - 1) / 16}em)`, fetchpriority: "high" },
				{ rel: "preload", href: this.$cloudFlare.getImage(picture.tablet.src, { width: picture.tablet.width, height: picture.tablet.height }), as: 'image', media: `(min-width: 43.75em) and (max-width: ${((62.5 * 16) - 1) / 16}em)`, fetchpriority: "high" },
				{ rel: "preload", href: this.$cloudFlare.getImage(picture.mobile.src, { width: picture.mobile.width, height: picture.mobile.height }), as: 'image', media: `(min-width: 0em) and (max-width: ${((43.75 * 16) - 1) / 16}em)`, fetchpriority: "high" },
			],
			script: [
				{
					type: "application/ld+json",
					json: this.pageContent.structuredData.localBusiness,
				},
				{
					type: "application/ld+json",
					json: this.pageContent.structuredData.autoRental,
				},
				{
					type: "application/ld+json",
					json: this.pageContent.structuredData.product,
				},
				{
					type: "application/ld+json",
					json: this.pageContent.structuredData.faqPage,
				},
			],
		}
	},
	validate(context) {
		return Boolean(context.store.getters['location/bySlug'][context.route.params.slug])
	},
	async asyncData(context) {
		let pageContent = null
		const { slug } = context.route.params

		const [pageMetaData, { default: _PageContent }, firstFiveFaqs] = await Promise.all([
			getPageMetaData(context.$strapi, slug),
			import(`~/static/data/locations/${slug}`),
			import("~/static/data/faq").then(({ faqs }) => faqs.filter((faq) => faq.section === slug).slice(0, 5)),
			context.store.dispatch("reviews/fetchCompany"),
			context.store.dispatch("reviews/fetchBranch"),
			context.store.dispatch("fetchHeading")
		])

		// We learned that by using import the response is being cached by node
		// kind of whats happening here: https://github.com/nodejs/modules/issues/307
		// this is why we deep clone the object
		pageContent = JSON.parse(JSON.stringify(_PageContent))

		const { title, description } = pageMetaData ?? pageContent.metaInfo
		const pageMeta = {
			title,
			description
		}

		if	(context.store.state.heading?.enabled) {
			// We don't replace title in location pages because title is the name of the location.
			const { headline, headlineLink, picture } = context.store.getters.headingPageHeader
			pageContent.pageHeader.headline	= headline
			pageContent.pageHeader.headlineLink	= headlineLink
			pageContent.pageHeader.picture = picture
		}

		const branchReview = context.store.getters['reviews/branchBySlug'](slug)

		pageContent.location = { ...context.store.getters['location/bySlug'][slug] }
		const locationHours = `${pageContent.location.openingTime}-${pageContent.location.closingTime}`
		pageContent.structuredData.localBusiness.openingHours = `Mo,Tu,We,Th,Fr,Sa,Su ${locationHours}`
		pageContent.structuredData.autoRental.openingHours = `mo-su ${locationHours}`

		if (branchReview) {
			pageContent.structuredData.autoRental.aggregateRating.reviewCount = branchReview.reviewCount
			pageContent.structuredData.autoRental.aggregateRating.ratingValue = branchReview.currentRating
			pageContent.structuredData.autoRental.aggregateRating.bestRating  = branchReview.maxRating
			pageContent.structuredData.product.aggregateRating.reviewCount = branchReview.reviewCount
			pageContent.structuredData.product.aggregateRating.ratingValue = branchReview.currentRating
			pageContent.structuredData.product.aggregateRating.bestRating  = branchReview.maxRating
		}

		pageContent.faqs = firstFiveFaqs
		pageContent.readMoreFaqsLink = { path: `/rental-car-locations/${slug}/faq/` }

		pageContent.structuredData = {
			...pageContent.structuredData,
			faqPage: getSchemaFaqPage(pageContent.faqs),
		}

		if (pageContent.location.dailyRateForDisplay && pageContent.location.dailyRateForDisplay > 0) {
			pageContent.structuredData.localBusiness.priceRange = "From NZ$" + pageContent.location.dailyRateForDisplay
			pageContent.structuredData.autoRental.priceRange = "From NZ$" + pageContent.location.dailyRateForDisplay
		}

		pageContent.location.flip = true
		pageContent.location.getDirections = pageContent.mapUrl
		pageContent.location.pickUpAndDropOff = pageContent.pickUpAndDropOff
		pageContent.location.shuttleOperatingHours = pageContent.shuttleOperatingHours

		pageContent.branchInformationTitle = `${pageContent.pageHeader.title} information`

		const showItinerary = !context.store.getters["itinerary/isEmpty"]

		return {
			pageMeta,
			pageContent,
			showItinerary,
		}
	},
}
</script>
